import { useState } from 'react';

import { useMutateExportHcAwtgRanBranches } from '@hooks/historyCheck/useMutateExportHcAwtgRanBranches';
import { useMutateExportHcBranches } from '@hooks/historyCheck/useMutateExportHcBranches';
import { useMutateGenerateHistoryCheckBranches } from '@hooks/historyCheck/useMutateGenerateHistoryCheckBranches';
import { useMutateRunHistoryProgressId } from '@hooks/historyCheck/useMutateRunHistoryProgressId';
import { useQueryHistoryCheckBranches } from '@hooks/historyCheck/useQueryHistoryCheckBranches';
import { useQueryHistoryCheckDates } from '@hooks/historyCheck/useQueryHistoryCheckDates';
import { useQueryHistoryCheckNodeTypes } from '@hooks/historyCheck/useQueryHistoryCheckNodeTypes';
import { useQueryHistoryCheckSiteIds } from '@hooks/historyCheck/useQueryHistoryCheckSiteIds';
import { useQueryHistoryCheckTechnologies } from '@hooks/historyCheck/useQueryHistoryCheckTechnologies';
import { useQueryHistoryCheckVendors } from '@hooks/historyCheck/useQueryHistoryCheckVendors';
import { useQueryHistoryProgressId } from '@hooks/historyCheck/useQueryHistoryProgressId';

import { ICMMavenirVendor } from '@interfaces/cmModule/cmModule';
import { TCMDefaultDTO, TMavenirDTO } from '@interfaces/cmModule/dto/cmModuleDTO';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';
import { IDateFromTo } from '@interfaces/common/range';

import { useMutateRunHistoryProgressAwtgRan } from './useMutateRunHistoryProgressAwtgRan';
import { useQueryHistoryCheckAwtgRanBranches } from './useQueryHistoryCheckAwtgRanBranches';
import { useQueryHistoryCheckAwtgRanConfigTypes } from './useQueryHistoryCheckAwtgRanConfigTypes';
import { useQueryHistoryCheckAwtgRanDates } from './useQueryHistoryCheckAwtgRanDates';
import { useQueryHistoryCheckAwtgRanNetworkElements } from './useQueryHistoryCheckAwtgRanNetworkElements';
import { useQueryHistoryCheckAwtgRanProgress } from './useQueryHistoryCheckAwtgRanProgress';
import { useQueryHistoryCheckConfigTypes } from './useQueryHistoryCheckConfigTypes';
import { useQueryHistoryCheckDatesOptions } from './useQueryHistoryCheckDatesOptions';
import { useQueryHistoryCheckNodes } from './useQueryHistoryCheckNodes';
import { useQueryHistoryCheckVersions } from './useQueryHistoryCheckVersions';

type TUseHistoryCheckParams = TDefaultDTO & TCMDefaultDTO & TMavenirDTO & ICMMavenirVendor & IDateFromTo & {
	isAwtgRanVendor?: boolean;
	networkElementIds?: string[];
}

export const useHistoryCheck = (params: TUseHistoryCheckParams) => {
	const [isProgressRun, setIsProgressRun] = useState(false);

	const {
		dateFrom,
		dateTo,
		nodeType,
		page,
		pageSize,
		searchValue,
		siteIds,
		sortDirection,
		sortFieldName,
		technology,
		vendor,
		isMavenirVendor,
		isAwtgRanVendor,
		nodes = [],
		networkElementIds = [],
		version,
		type,
	} = params;

	const {
		generateBranches,
		generatedBranches,
		isLoadingBranchesGenerated,
	} = useMutateGenerateHistoryCheckBranches(setIsProgressRun);

	const { generateProgressId, progressId } = useMutateRunHistoryProgressId({
		nodes,
		version,
		type,
		technology,
		nodeType,
		siteIds,
		vendor,
		dateFrom,
		dateTo,
		isMavenirVendor,
		setIsProgressRun,
		generateBranches,
	});

	const { progressData } = useQueryHistoryProgressId({
		progressId,
		isProgressRun,
		setIsProgressRun,
	});

	const {
		generateProgressId: generateAwtgRanProgressId,
		progressId: awtgRanProgressId,
	} = useMutateRunHistoryProgressAwtgRan({
		vendor,
		configType: type,
		networkElementIds,
		dateFrom,
		dateTo,
		setIsProgressRun,
	});

	const { progressData: awtgRanProgressData } = useQueryHistoryCheckAwtgRanProgress({
		vendor,
		progressId: awtgRanProgressId,
		isProgressRun,
		setIsProgressRun,
	});

	const { dumpId } = generatedBranches || {};

	const {
		branchData,
		isLoadingBranchData,
	} = useQueryHistoryCheckBranches({
		page,
		pageSize,
		sortDirection,
		progressId,
		progress: progressData,
		sortFieldName,
		searchValue,
		dumpHistoryId: dumpId,
	});

	const {
		branchData: awtgRanBranchData,
		isLoadingBranchData: isLoadingAwtgRanBranchData,
	} = useQueryHistoryCheckAwtgRanBranches({
		vendor,
		page,
		pageSize,
		sortDirection,
		progressId: awtgRanProgressId,
		progress: awtgRanProgressData,
		sortFieldName,
		searchValue,
	});

	const {
		vendorsOptions,
		isLoadingVendorsOptions,
	} = useQueryHistoryCheckVendors();

	const {
		technologiesOptions,
		isLoading: isLoadingTechnologiesOptions,
	} = useQueryHistoryCheckTechnologies({
		vendor,
	});

	const {
		nodesTypesOptions,
		isLoadingNodeTypesOptions,
	} = useQueryHistoryCheckNodeTypes({
		vendor,
		technology,
		isMavenirVendor,
	});

	const {
		siteIdsOptions,
		isLoadingSiteIdsOptions,
	} = useQueryHistoryCheckSiteIds({
		vendor,
		technology,
		nodeType,
		isMavenirVendor,
	});

	const {
		nodesOptions,
		isLoadingNodesOptions,
	} = useQueryHistoryCheckNodes({ vendor, isMavenirVendor });

	const {
		configTypeOptions,
		isLoadingConfigTypeOptions,
	} = useQueryHistoryCheckConfigTypes({
		vendor,
		isMavenirVendor,
		nodes,
		version,
	});

	const {
		versionsOptions,
		isLoadingVersionsOptions,
	} = useQueryHistoryCheckVersions({
		vendor,
		nodes,
		isMavenirVendor,
	});

	const {
		dates,
		isLoadingDates,
	} = useQueryHistoryCheckDates({
		vendor,
		technology,
		nodeType,
		siteIds,
		isMavenirVendor,
	});

	const {
		dateOptions,
		isLoadingDateOptions,
	} = useQueryHistoryCheckDatesOptions({
		vendor,
		type: type,
		nodes,
		version,
		isMavenirVendor,
	});

	const {
		configTypeOptions: awtgRanConfigTypeOptions,
		isLoading: isLoadingAwtgRanConfigTypeOptions,
	} = useQueryHistoryCheckAwtgRanConfigTypes({
		vendor,
	});

	const {
		networkElementsOptions,
		isLoading: isLoadingNetworkElementsOptions,
	} = useQueryHistoryCheckAwtgRanNetworkElements({
		vendor,
		configType: type,
	});

	const {
		dateOptions: awtgRanDateOptions,
		isLoading: isLoadingAwtgRanDateOptions,
	} = useQueryHistoryCheckAwtgRanDates({
		vendor,
		configType: type,
		networkElementIds,
	});

	const { exportBranches } = useMutateExportHcBranches();
	const { exportBranches: exportAwtgRanBranches } = useMutateExportHcAwtgRanBranches();

	return {
		generateProgressId: isAwtgRanVendor ? generateAwtgRanProgressId : generateProgressId,
		progressId: isAwtgRanVendor ? awtgRanProgressId : progressId,
		dumpId: isAwtgRanVendor ? awtgRanProgressData?.dumpId : progressData?.dumpId,
		progressData: isAwtgRanVendor ? (awtgRanProgressData || {}) : (progressData || {}),
		branchData: isAwtgRanVendor ? (awtgRanBranchData?.items || []) : (branchData?.items || []),
		branchDataTotalCount: isAwtgRanVendor ? (awtgRanBranchData?.count || 0) : (branchData?.count || 0),
		vendorsOptions: vendorsOptions || [],
		technologiesOptions: technologiesOptions || [],
		nodesTypesOptions: nodesTypesOptions || [],
		datesOptions: isMavenirVendor
			? dateOptions
			: isAwtgRanVendor
				? awtgRanDateOptions
				: dates,
		versionsOptions: versionsOptions,
		nodesOptions: nodesOptions,
		configTypeOptions: isAwtgRanVendor ? awtgRanConfigTypeOptions : configTypeOptions,
		networkElementsOptions: networkElementsOptions || [],
		siteIdsOptions: siteIdsOptions || [],
		isLoadingBranchData: isAwtgRanVendor
			? (isLoadingAwtgRanBranchData || isProgressRun)
			: (isLoadingBranchData || isLoadingBranchesGenerated || isProgressRun),
		isLoadingOptions: isLoadingVendorsOptions
			|| isLoadingTechnologiesOptions
			|| isLoadingNodeTypesOptions
			|| isLoadingDateOptions
			|| isLoadingDates
			|| isLoadingVersionsOptions
			|| isLoadingNodesOptions
			|| isLoadingConfigTypeOptions
			|| isLoadingSiteIdsOptions
			|| isLoadingAwtgRanConfigTypeOptions
			|| isLoadingNetworkElementsOptions
			|| isLoadingAwtgRanDateOptions,
		exportBranches,
		exportAwtgRanBranches,
	};
};
